// One calibration item for blind labelling (TOM-141; ADR 0234 §3): the case as
// the judge saw it, the reply it graded, and the label form. The judge's
// verdict and reasons render only after this person's label exists, and only
// from verdictToShow, which answers null before then whatever the item holds.
// Styles: styles/admin-eval-labels.css and styles/admin-evals.css.

import { useState, type FormEvent, type ReactNode } from 'react';

import { formatDate } from '../format';
import { EvalBadge } from './EvalBits';
import {
  CALIBRATION_NOTE_MAX_CHARS,
  labelChoices,
  labelQuestion,
  labelToShow,
  noteValue,
  progressText,
  progressWidth,
  verdictText,
  verdictToShow,
  type CalibrationCapability,
  type CalibrationCaseSnapshot,
  type CalibrationItemForLabel,
  type CalibrationLabelRecord,
  type CalibrationLabelSaved,
  type CalibrationVerdict,
  type CalibrationVerdictField,
} from './labels';

/** This person's progress through a set: a bar and "3 of 6 labelled". */
export function ProgressBar({ labelled, total }: { labelled: number; total: number }) {
  return (
    <span className="eval-label-progress" title={progressText(labelled, total)}>
      <span className="eval-label-progress-bar" aria-hidden="true">
        <span style={{ width: progressWidth(labelled, total) }} />
      </span>
      <span className="eval-sub">{progressText(labelled, total)}</span>
    </span>
  );
}

function capabilityText(c: CalibrationCapability): string {
  switch (c.kind) {
    case 'served':
      return c.sees !== undefined ? `sees ${c.sees}` : `finds no ${c.lacks ?? 'data'}`;
    case 'stub-write':
      return `write stubbed${c.note !== undefined ? `: ${c.note}` : ''}`;
    case 'failed-read':
      return `fails (${c.reason}); cannot see ${c.lacks}`;
  }
}

/** The frozen case: what the judge read beside the reply. */
export function CaseSnapshotView({ snapshot }: { snapshot: CalibrationCaseSnapshot }) {
  return (
    <div className="eval-label-case" data-testid="eval-label-case">
      {snapshot.prompt !== null ? (
        <>
          <h3>The user&apos;s message</h3>
          <div className="eval-reply" data-testid="eval-label-prompt">
            {snapshot.prompt}
          </div>
        </>
      ) : null}
      {snapshot.situation !== null ? (
        <>
          <h3>The situation</h3>
          <div className="eval-reply" data-testid="eval-label-situation">
            {snapshot.situation}
          </div>
        </>
      ) : null}
      {snapshot.surfaceBrief !== null ? (
        <>
          <h3>What the judge was told about the surface</h3>
          <p className="eval-label-text">{snapshot.surfaceBrief}</p>
        </>
      ) : null}
      <h3>Rubric</h3>
      <p className="eval-label-text" data-testid="eval-label-rubric">
        {snapshot.rubric}
      </p>
      {snapshot.facts.length > 0 ? (
        <>
          <h3>Recorded facts</h3>
          <ul className="eval-facts" data-testid="eval-label-facts">
            {snapshot.facts.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </>
      ) : null}
      {snapshot.capabilities !== undefined && snapshot.capabilities.length > 0 ? (
        <details className="eval-drawer-details">
          <summary>What the assistant could read ({snapshot.capabilities.length} tools)</summary>
          <ul className="eval-facts">
            {snapshot.capabilities.map((c) => (
              <li key={`${c.kind}:${c.tool}`}>
                <code>{c.tool}</code> {capabilityText(c)}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

/** The label form. It holds no verdict and asks for none. */
export function LabelForm({
  field,
  saving,
  error,
  onSave,
}: {
  field: CalibrationVerdictField;
  saving: boolean;
  error: string | null;
  onSave: (label: boolean, note: string | null) => void;
}) {
  const [choice, setChoice] = useState<boolean | null>(null);
  const [note, setNote] = useState('');
  const [invalid, setInvalid] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (choice === null) {
      setInvalid('Pick an answer first.');
      return;
    }
    const checked = noteValue(note);
    if (!checked.ok) {
      setInvalid(checked.error);
      return;
    }
    setInvalid(null);
    onSave(choice, checked.note);
  }

  const message = invalid ?? error;
  return (
    <form className="eval-label-form" onSubmit={submit} data-testid="eval-label-form">
      <fieldset disabled={saving}>
        <legend>{labelQuestion(field)}</legend>
        <div className="eval-label-choices" role="radiogroup">
          {labelChoices(field).map((c) => (
            <label
              key={String(c.value)}
              className={`eval-label-choice${choice === c.value ? ' is-picked' : ''}`}
            >
              <input
                type="radio"
                name="label"
                value={String(c.value)}
                checked={choice === c.value}
                onChange={() => setChoice(c.value)}
                data-testid={`eval-label-choice-${c.value}`}
              />
              {c.text}
            </label>
          ))}
        </div>
        <label className="eval-label-note">
          Note (optional)
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={CALIBRATION_NOTE_MAX_CHARS}
            rows={3}
            placeholder="Why, if it was a close call"
            data-testid="eval-label-note"
          />
        </label>
        <div className="eval-label-save">
          <button
            type="submit"
            className="admin-button-affirm"
            disabled={choice === null || saving}
            data-testid="eval-label-save"
          >
            {saving ? 'Saving…' : 'Save label'}
          </button>
          <span className="admin-muted eval-sub">
            Saved once, never changed. The judge&apos;s verdict shows after you save.
          </span>
        </div>
        {message !== null ? (
          <p className="eval-error-text" role="alert" data-testid="eval-label-error">
            {message}
          </p>
        ) : null}
      </fieldset>
    </form>
  );
}

function YourLabel({
  label,
  field,
}: {
  label: CalibrationLabelRecord;
  field: CalibrationVerdictField;
}) {
  return (
    <div className="eval-label-yours" data-testid="eval-label-yours">
      <p>
        <span className="admin-muted">Your label:</span>{' '}
        <strong data-testid="eval-label-value">{verdictText(field, label.label)}</strong>{' '}
        <span className="admin-muted eval-sub">saved {formatDate(label.createdAt)}</span>
      </p>
      {label.note !== null ? (
        <p className="eval-label-text" data-testid="eval-label-saved-note">
          {label.note}
        </p>
      ) : null}
    </div>
  );
}

/** The judge's verdict and every grading's reasons: shown only after a label exists. */
export function VerdictPanel({
  verdict,
  label,
}: {
  verdict: CalibrationVerdict;
  label: CalibrationLabelRecord;
}) {
  const agree = verdict.verdict === label.label;
  return (
    <section className="eval-label-verdict" data-testid="eval-label-verdict">
      <h3>The judge&apos;s verdict</h3>
      <p>
        <strong data-testid="eval-label-judge-verdict">
          {verdictText(verdict.verdictField, verdict.verdict)}
        </strong>{' '}
        <EvalBadge tone={agree ? 'succeeded' : 'warn'} testId="eval-label-agreement">
          {agree ? 'You agree' : 'You disagree'}
        </EvalBadge>
      </p>
      <ul className="eval-verdicts" data-testid="eval-label-gradings">
        {verdict.gradings.map((g, i) => (
          <li key={i}>
            <span>
              <code>{g.judgeModel ?? g.source}</code>
              {g.judgeVersion !== null ? (
                <span className="admin-muted"> @ {g.judgeVersion}</span>
              ) : null}
              {g.grading === 2 ? <span className="admin-muted"> · second grading</span> : null}
              {i === 0 ? <span className="admin-muted"> · primary</span> : null}
              {': '}
              <strong>
                {g.verdict === null ? 'failed' : verdictText(verdict.verdictField, g.verdict)}
              </strong>
            </span>
            <span className="eval-reason">{g.reason}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The item: the case, the reply, then the form until a label exists, and the
 * label with the judge's verdict after.
 */
export function LabelItemView({
  item,
  saved,
  saving,
  error,
  onSave,
  next,
}: {
  item: CalibrationItemForLabel;
  saved: CalibrationLabelSaved | null;
  saving: boolean;
  error: string | null;
  onSave: (label: boolean, note: string | null) => void;
  /** What to do next, shown under the verdict once the item is labelled. */
  next?: ReactNode;
}) {
  const field = item.case.verdictField;
  const label = labelToShow(item, saved);
  const verdict = verdictToShow(item, saved);
  return (
    <div className="eval-label-grid">
      <section className="eval-card">
        <h2 className="eval-label-h2">The case</h2>
        <CaseSnapshotView snapshot={item.case} />
      </section>
      <section className="eval-card eval-label-side">
        <h2 className="eval-label-h2">The reply</h2>
        <div className="eval-reply eval-label-reply" data-testid="eval-label-reply">
          {item.reply.length > 0 ? item.reply : <span className="admin-muted">Empty reply.</span>}
        </div>
        {label === null ? (
          <LabelForm field={field} saving={saving} error={error} onSave={onSave} />
        ) : (
          <>
            <YourLabel label={label} field={field} />
            {verdict !== null ? <VerdictPanel verdict={verdict} label={label} /> : null}
            {next !== undefined ? <div className="eval-label-next">{next}</div> : null}
          </>
        )}
      </section>
    </div>
  );
}
